class PdpAddonPopup extends HTMLElement {
  constructor() {
    super();
    this.dialog = null;
    this.closeButton = null;
    this._handleKeyDown = this._handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    this.closeButton = this.querySelector(".popup-content__close");

    const title = this.querySelector(".popup-content__title");
    if (title && !title.id) {
      title.id = this._getTitleId();
    }
    const body = this.querySelector(".popup-content__body");
    if (body && !body.id) {
      body.id = this._getBodyId();
    }

    // Only set ARIA attributes if not already present (for hydration or SSR fallback)
    if (this.dialog) {
      if (!this.dialog.hasAttribute("role")) {
        this.dialog.setAttribute("role", "dialog");
      }
      if (!this.dialog.hasAttribute("aria-modal")) {
        this.dialog.setAttribute("aria-modal", "true");
      }
      if (!this.dialog.hasAttribute("aria-labelledby")) {
        this.dialog.setAttribute("aria-labelledby", this._getTitleId());
      }
      if (!this.dialog.hasAttribute("aria-describedby")) {
        this.dialog.setAttribute("aria-describedby", this._getBodyId());
      }
    }

    if (this.closeButton) {
      this._closeButtonHandler = () => this.close();
      this.closeButton.addEventListener("click", this._closeButtonHandler);
    }
    if (this.dialog) {
      this._dialogCloseHandler = () => this._onDialogClose();
      this.dialog.addEventListener("close", this._dialogCloseHandler);

      this._dialogClickHandler = (e) => {
        if (e.target === this.dialog) {
          this.close();
        }
      };
      this.dialog.addEventListener("click", this._dialogClickHandler);
    }

    this._initPopupMediaGallery();
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this._handleKeyDown);

    if (this.closeButton && this._closeButtonHandler) {
      this.closeButton.removeEventListener("click", this._closeButtonHandler);
      this._closeButtonHandler = null;
    }

    if (this.dialog && this._dialogCloseHandler) {
      this.dialog.removeEventListener("close", this._dialogCloseHandler);
      this._dialogCloseHandler = null;
    }
    if (this.dialog && this._dialogClickHandler) {
      this.dialog.removeEventListener("click", this._dialogClickHandler);
      this._dialogClickHandler = null;
    }

    if (this.dialog && (this.dialog.open || this.dialog.hasAttribute("open"))) {
      document.body.style.overflow = "";
    }
  }

  _initPopupMediaGallery() {
    const mainImage = this.querySelector("[data-popup-main-image]");
    const thumbs = this.querySelectorAll("[data-popup-thumb]");
    if (!mainImage || !thumbs.length) return;
    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", (e) => {
        e.preventDefault();
        mainImage.src = thumb.getAttribute("data-popup-image");
        mainImage.alt = thumb.alt;

        thumbs.forEach((t) => t.classList.remove("active"));
        thumb.classList.add("active");
      });
    });
    thumbs[0].classList.add("active");
  }

  open() {
    if (!this.dialog) return;
    if (typeof this.dialog.showModal === "function") {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute("open", "");
    }
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this._handleKeyDown);
    this._trapFocus();
  }

  close() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === "function") {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute("open");
    }
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this._handleKeyDown);
  }

  _onDialogClose() {
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this._handleKeyDown);
  }

  _handleKeyDown(e) {
    if (e.key === "Escape") {
      this.close();
    }
    if (e.key === "Tab") {
      this._trapFocus(e);
    }
  }

  _trapFocus(e) {
    if (!this.dialog) return;
    const focusable = this.dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!e) {
      first.focus();
      return;
    }
    if (e.shiftKey) {
      if (document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  }

  _getTitleId() {
    return this.id ? `${this.id}-popup-title` : "popup-title";
  }

  _getBodyId() {
    return this.id ? `${this.id}-popup-body` : "popup-body";
  }
}

if (!customElements.get("pdp-addon-popup")) {
  customElements.define("pdp-addon-popup", PdpAddonPopup);
}